import { useState, useCallback, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useTeam } from '../../../contexts/TeamContext'
import { useNodes } from '../../../contexts/NodesContext'
import { useDomains } from '../../../contexts/DomainsContext'
import useDomainsResources from '../../../shared/resources/api/v1/admin/networking/useDomainsResources'
import { useAddresses } from '../../../contexts/AddressesContext'
import { useConfirmationDialog } from '../../../system/ConfirmationDialogProvider'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'
import Page from '../../../shared/components/layouts/Page'
import PageHeader from '../../../shared/components/layouts/PageHeader'
import PageBody from '../../../shared/components/layouts/PageBody'
import FormDrawer from '../../../shared/components/drawers/FormDrawer'
import DomainForm from './components/DomainForm'
import Flex from "../../../shared/components/layouts/Flex.jsx";

const getStatusColor = (status) => {
  switch (status) {
    case 'confirmed':
      return 'success'
    case 'pending':
      return 'warning'
    default:
      return 'default'
  }
}

function DomainIndexPage() {
  const { teamSlug } = useParams()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { team } = useTeam()
  const { nodesById, loading: nodesLoading } = useNodes()
  const { domains, loading: domainsLoading, error: domainsError, reload: reloadDomains } = useDomains()
  const { create, update, destroy, check } = useDomainsResources(teamSlug)
  const { addressesById, loading: addressesLoading } = useAddresses()
  const { openConfirmationDialog } = useConfirmationDialog()

  const [drawer, setDrawer] = useState({ open: false, domain: null })
  const [checkingDomainId, setCheckingDomainId] = useState(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    return () => { mountedRef.current = false }
  }, [])

  const loading = nodesLoading || domainsLoading || addressesLoading

  const handleOpenDrawer = (domain = null) => {
    setDrawer({ open: true, domain })
  }

  const handleCloseDrawer = () => {
    setDrawer({ open: false, domain: null })
  }

  const handleFormSuccess = () => {
    handleCloseDrawer()
    reloadDomains()
  }

  const handleDeleteDomain = useCallback((domain) => {
    openConfirmationDialog({
      title: <FormattedMessage id="domains.delete.title" />,
      message: <FormattedMessage id="domains.delete.message" />,
      onConfirm: () => destroy(domain.id)
        .then(() => {
          reloadDomains()
        })
        .catch(() => {
          enqueueSnackbar(formatMessage({ id: 'domains.delete.error' }), { variant: 'error' })
        }),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroy, reloadDomains, enqueueSnackbar, formatMessage])

  const handleCheckDomain = useCallback((domain) => {
    setCheckingDomainId(domain.id)
    const minDelay = new Promise(resolve => setTimeout(resolve, 2000))

    Promise.all([check(domain.id), minDelay])
      .then(() => {
        if (!mountedRef.current) return
        reloadDomains()
      })
      .catch(() => {
        if (!mountedRef.current) return
        enqueueSnackbar(formatMessage({ id: 'domains.check.error' }), { variant: 'error' })
      })
      .finally(() => {
        if (!mountedRef.current) return
        setCheckingDomainId(null)
      })
  }, [check, reloadDomains, enqueueSnackbar, formatMessage])

  if (loading) return <LoadingState />
  if (domainsError) return <ErrorState messageId="domains.error" />

  return (
    <Page>
      <PageHeader
        title={<FormattedMessage id="domains.title" />}
        entities={{ team }}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDrawer()}
          >
            <FormattedMessage id="domains.actions.create" />
          </Button>
        }
      />
      <PageBody>
        {domains.length === 0 ? (
          <Typography color="text.secondary">
            <FormattedMessage id="domains.empty" />
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <FormattedMessage id="domains.index.columns.status" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="domains.index.columns.domain" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="domains.index.columns.node" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="domains.index.columns.address" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="domains.index.columns.resolvedAddress" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="domains.index.columns.lastChecked" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="domains.index.columns.actions" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {domains.map((domain) => {
                  const node = nodesById[domain.nodeId]
                  const address = addressesById[domain.addressId]

                  return (
                    <TableRow key={domain.id}>
                      <TableCell>
                        {checkingDomainId === domain.id ? (
                          <Chip
                            label={
                              <Flex alignItems="center">
                                <FormattedMessage id="domains.status.checking" />
                                <CircularProgress size={12} color="inherit" sx={{ ml: 1 }} />
                              </Flex>
                            }
                            color="info"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label={<FormattedMessage id={`domains.status.${domain.status}`} />}
                            color={getStatusColor(domain.status)}
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {domain.domain}
                        </Typography>
                      </TableCell>
                      <TableCell>{node?.name || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {address?.address || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {domain.resolvedAddress || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {domain.lastCheckedAt ? (
                          <FormattedDate
                            value={domain.lastCheckedAt}
                            year="numeric"
                            month="short"
                            day="numeric"
                            hour="numeric"
                            minute="numeric"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleCheckDomain(domain)}
                          disabled={checkingDomainId === domain.id}
                        >
                          {checkingDomainId === domain.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <RefreshIcon fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDrawer(domain)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteDomain(domain)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageBody>

      <FormDrawer
        open={drawer.open}
        onClose={handleCloseDrawer}
        titleId={drawer.domain ? 'domains.edit.title' : 'domains.create.title'}
      >
        <DomainForm
          domain={drawer.domain}
          createDomain={create}
          updateDomain={update}
          onSuccess={handleFormSuccess}
          onCancel={handleCloseDrawer}
        />
      </FormDrawer>
    </Page>
  )
}

export default DomainIndexPage
